import { BACKGROUND, BODY_CONTENT, CONTENT, FOOTER, GALLERY, HEADER } from '@opengov/stories-shared/constants';
import { IStory, IStoryContent, IStoryCustomColors } from '@opengov/stories-shared/types';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import ImageIcon from '@mui/icons-material/Image';
import PaletteIcon from '@mui/icons-material/Palette';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useState } from 'react';

import { editor as editorMessages } from '../../../../locale/stories.en.locale.json';
import {
  IEditorMessages,
  IModalMessages,
  ISettings,
  ISettingsColors,
  IStoryEditorContentSpacing,
  IUpdateCustomColorProps,
  UpdateCustomColorsResponse
} from '../../../../types';
import { ADD_BACKGROUND, DELETE_CONTENT } from '../../../constants';
import { getSubtypeColorsByStoryContentType } from '../../../utils/editor';
import { ColorPicker } from '../../StoriesPage/ColorPicker';
import { SpacingMenu } from '../SpacingMenu';

import { styles } from './ContentMenu.styles';

export type IContentMenuProps = {
  document: Document;
  canAddProposalContent?: boolean;
  isReadOnlySection?: boolean;
  window: Window;
  isReadOnlyMode: boolean;
  isTemplate: boolean;
  canEditSpacing: boolean;
  canEditPageBreaks: boolean;
  storyContent: IStoryContent;
  storyCustomColors: IStoryCustomColors;
  totalStoryContents: number;
  story: IStory;
  storyContents: IStoryContent[];
  storyContentArray: {
    format: string;
    thumbnail: any; // SVG file
  }[];
  messages: IEditorMessages;
  settings: ISettings;
  handleFormatClick(format: string, fonts: { headline: string; paragraph: string }, colors: ISettingsColors): void;
  handleColorClick(event: React.MouseEvent<HTMLElement>, color: string): void;
  handleSaveSpacing(spacing: IStoryEditorContentSpacing): void;
  handleOrderClick(up: boolean): void;
  handleAddSlide(): void;
  handleUpdateModal(modal: string, storyContent: IStoryContent): void;
  updateStoryCustomColors(props: IUpdateCustomColorProps): UpdateCustomColorsResponse;
  handlePreviewSection?(): void;
  modalMessages: IModalMessages;
};

export function ContentMenu(props: IContentMenuProps) {
  const {
    story,
    isReadOnlyMode,
    isReadOnlySection,
    isTemplate,
    canAddProposalContent,
    storyContent,
    storyContent: { _id, type, group, content = '', order, format },
    totalStoryContents,
    storyContentArray,
    storyCustomColors,
    messages,
    modalMessages,
    settings,
    settings: { brand: { fonts, colors } },
    handleFormatClick,
    handleColorClick,
    handleOrderClick,
    handleAddSlide,
    handleSaveSpacing,
    handleUpdateModal,
    handlePreviewSection,
    updateStoryCustomColors
  } = props;

  const popoverContainer = props.document.body;

  const [formatAnchor, setFormatAnchor] = useState<HTMLElement | null>(null);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [spacingAnchor, setSpacingAnchor] = useState<HTMLElement | null>(null);

  const closeAllPopovers = useCallback(() => {
    setFormatAnchor(null);
    setColorAnchor(null);
    setSpacingAnchor(null);
  }, []);

  useEffect(() => {
    const win = props.window;
    win.addEventListener('scroll', closeAllPopovers);
    return () => win.removeEventListener('scroll', closeAllPopovers);
  }, [props.window, closeAllPopovers]);

  const onAddColor = useCallback(
    (color: string) => {
      const backwardCompatibleType = type === CONTENT ? BODY_CONTENT : type;
      const newHeader = storyCustomColors[HEADER][BACKGROUND];
      const newBodyContent = storyCustomColors[BODY_CONTENT][BACKGROUND];
      const newFooter = storyCustomColors[FOOTER][BACKGROUND];
      let newColors: IStoryCustomColors;

      switch (backwardCompatibleType) {
        case HEADER:
          if (!newHeader.includes(color)) {
            newHeader.push(color);
          }
          newColors = {
            ...storyCustomColors,
            [HEADER]: {
              ...storyCustomColors[HEADER],
              [BACKGROUND]: newHeader
            }
          };
          break;
        case BODY_CONTENT:
          if (!newBodyContent.includes(color)) {
            newBodyContent.push(color);
          }
          newColors = {
            ...storyCustomColors,
            [BODY_CONTENT]: {
              ...storyCustomColors[BODY_CONTENT],
              [BACKGROUND]: newBodyContent
            }
          };
          break;
        case FOOTER:
          if (!newFooter.includes(color)) {
            newFooter.push(color);
          }
          newColors = {
            ...storyCustomColors,
            [FOOTER]: {
              ...storyCustomColors[FOOTER],
              [BACKGROUND]: newFooter
            }
          };
          break;
        default:
          newColors = storyCustomColors;
      }
      updateStoryCustomColors({ customColors: newColors, story, isTemplate });
    },
    [story, storyCustomColors, type, updateStoryCustomColors, isTemplate]
  );

  const onRemoveColor = useCallback(
    (color: string) => {
      const backwardCompatibleType = type === CONTENT ? BODY_CONTENT : type;
      const customColors = storyCustomColors[backwardCompatibleType][BACKGROUND];
      const index = customColors.indexOf(color);

      if (index > -1) customColors.splice(index, 1);

      const newColors: IStoryCustomColors = {
        ...storyCustomColors,
        [backwardCompatibleType]: {
          ...storyCustomColors[backwardCompatibleType],
          [BACKGROUND]: customColors
        }
      };

      updateStoryCustomColors({ customColors: newColors, story, isTemplate });
    },
    [story, storyCustomColors, type, updateStoryCustomColors, isTemplate]
  );

  const getInitialSpacing = useCallback((): IStoryEditorContentSpacing => {
    let paddingTop = NaN;
    let paddingBottom = NaN;
    let breakBefore = false;
    const fragment = document.createRange().createContextualFragment(props.storyContent.content);
    const container =
      (fragment.firstElementChild as HTMLElement) ||
      (fragment.firstChild as HTMLElement) ||
      (fragment.children[0] as HTMLElement);
    paddingTop = parseFloat(container.style.paddingTop);
    paddingBottom = parseFloat(container.style.paddingBottom);
    breakBefore = !!container.style.breakBefore;

    return {
      paddingTop: isNaN(paddingTop) ? undefined : paddingTop,
      paddingBottom: isNaN(paddingBottom) ? undefined : paddingBottom,
      breakBefore
    };
  }, [props.storyContent.content]);

  const handleChangeSpacing = useCallback(
    (spacing: IStoryEditorContentSpacing) => {
      handleSaveSpacing(spacing);
      setSpacingAnchor(null);
    },
    [handleSaveSpacing]
  );

  const backwardCompatibleType = type === CONTENT ? BODY_CONTENT : type;
  const hasBackgroundImage = content && content.indexOf('background-image:') > -1;
  const formatWidth =
    storyContentArray.length >= 3 ? '540px' : `${storyContentArray.length * 165 + storyContentArray.length * 15}px`;
  const { swatch, customSwatch } = getSubtypeColorsByStoryContentType({
    storyContentType: type,
    colors: settings.brand.colors,
    storyCustomColors,
    colorsSubtype: BACKGROUND
  });

  const formatPanel = (
    <>
      <Tooltip title={messages.layout} placement="bottom">
        <IconButton
          aria-label={messages.layout}
          onClick={(e) => setFormatAnchor(e.currentTarget)}
          sx={styles.iconButton}
          size="small"
        >
          <ViewColumnIcon aria-hidden={true} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        data-test="story-format-dropdown-menu"
        open={Boolean(formatAnchor)}
        anchorEl={formatAnchor}
        onClose={() => setFormatAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        container={popoverContainer}
        PaperProps={{ sx: styles.popoverPaper }}
      >
        <Box className="formats" sx={[styles.formatsPanel, { width: formatWidth }]}>
          {storyContentArray.map((s, idx) => (
            <Box
              key={`${s.format}-${idx}`}
              component="button"
              type="button"
              aria-label={s.format}
              onClick={() => {
                if (format !== s.format) handleFormatClick(s.format, fonts, colors);
                setFormatAnchor(null);
              }}
              sx={styles.formatThumbnailButton}
            >
              <img src={s.thumbnail} alt="" />
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );

  const formatPreview = canAddProposalContent ? (
    <Tooltip title={messages.preview} placement="bottom">
      <Box
        component="div"
        data-test="preview-section-button"
        id={`format-preview-wrapper-${_id}`}
        sx={isReadOnlySection ? styles.activePreview : styles.previewIcon}
      >
        <IconButton
          data-test="undoButton"
          aria-label={messages.preview}
          onClick={handlePreviewSection}
          sx={styles.previewIconButton}
          size="small"
        >
          {isReadOnlySection ? <VisibilityOffIcon aria-hidden={true} /> : <VisibilityIcon aria-hidden={true} />}
        </IconButton>
      </Box>
    </Tooltip>
  ) : null;

  const colorLabel = hasBackgroundImage ? messages.overlayColor : messages.backgroundColor;
  const colorPanel = (
    <>
      <Tooltip title={colorLabel} placement="bottom">
        <IconButton
          aria-label={colorLabel}
          onClick={(e) => setColorAnchor(e.currentTarget)}
          sx={styles.iconButton}
          size="small"
        >
          <PaletteIcon aria-hidden={true} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        data-test="story-color-dropdown-menu"
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        container={popoverContainer}
        PaperProps={{ sx: styles.popoverPaper }}
      >
        <ColorPicker
          isEditable={false}
          label={editorMessages.brandingPanel}
          colorSwatch={swatch}
          onAddColor={onAddColor}
          onRemoveColor={onRemoveColor}
          onColorClick={(event: React.MouseEvent<HTMLElement>, color: string) => {
            handleColorClick(event, color);
            setColorAnchor(null);
          }}
          key="background"
        />
        <ColorPicker
          isEditable={true}
          label={editorMessages.customPanel}
          colorSwatch={customSwatch}
          onAddColor={onAddColor}
          onRemoveColor={onRemoveColor}
          onColorClick={(event: React.MouseEvent<HTMLElement>, color: string) => {
            handleColorClick(event, color);
            setColorAnchor(null);
          }}
          key="custom"
        />
      </Popover>
    </>
  );

  const spacingPanel = (
    <>
      <Tooltip title={editorMessages.spacing} placement="bottom">
        <IconButton
          aria-label={editorMessages.spacing}
          data-test="story-spacing-menu-button"
          onClick={(e) => setSpacingAnchor(e.currentTarget)}
          sx={styles.iconButton}
          size="small"
        >
          <FormatLineSpacingIcon aria-hidden={true} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        data-test="story-spacing-dropdown-menu"
        open={Boolean(spacingAnchor)}
        anchorEl={spacingAnchor}
        onClose={() => setSpacingAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        container={popoverContainer}
        PaperProps={{ sx: styles.popoverPaper }}
      >
        <SpacingMenu
          story={story}
          canEditPageBreaks={props.canEditPageBreaks}
          initialSpacing={getInitialSpacing()}
          onChangeSpacing={handleChangeSpacing}
          onCancel={() => setSpacingAnchor(null)}
          messages={modalMessages}
        />
      </Popover>
    </>
  );

  if (isReadOnlyMode) {
    return null;
  }

  if (backwardCompatibleType === BODY_CONTENT) {
    return (
      <Box component="div" className="edit-content" sx={styles.contentDisplay}>
        {order > 2 && (
          <Tooltip title={messages.moveUp} placement="bottom">
            <IconButton
              aria-label={messages.moveUp}
              onClick={() => handleOrderClick(true)}
              sx={styles.iconButton}
              size="small"
              data-test="story-content-move-up"
            >
              <ArrowUpwardIcon aria-hidden={true} fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {order < totalStoryContents - 1 && (
          <Tooltip title={messages.moveDown} placement="bottom">
            <IconButton
              aria-label={messages.moveDown}
              onClick={() => handleOrderClick(false)}
              sx={styles.iconButton}
              size="small"
              data-test="story-content-move-down"
            >
              <ArrowDownwardIcon aria-hidden={true} fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {storyContent && formatPreview}
        {formatPanel}
        {colorPanel}
        {props.canEditSpacing && spacingPanel}
        {group === GALLERY && (
          <Tooltip title={messages.addSlide} placement="bottom">
            <IconButton
              aria-label={messages.addSlide}
              onClick={handleAddSlide}
              sx={styles.iconButton}
              size="small"
              data-test="story-content-add-slide"
            >
              <AddIcon aria-hidden={true} fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={messages.deleteText} placement="bottom">
          <IconButton
            aria-label={messages.deleteText}
            onClick={() => handleUpdateModal(DELETE_CONTENT, storyContent)}
            sx={styles.iconButton}
            size="small"
            data-test="story-content-delete"
          >
            <DeleteIcon aria-hidden={true} fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box component="div" className="edit-content" sx={styles.contentDisplay}>
      {storyContent && formatPreview}
      {formatPanel}
      {colorPanel}
      {props.canEditSpacing && spacingPanel}
      {backwardCompatibleType === HEADER && (
        <Tooltip title={messages.backgroundImage} placement="bottom">
          <IconButton
            aria-label={messages.backgroundImage}
            onClick={() => handleUpdateModal(ADD_BACKGROUND, storyContent)}
            sx={styles.iconButton}
            size="small"
          >
            <ImageIcon aria-hidden={true} fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
